import {
  DeleteRounded as DeleteRoundedIcon,
  EditRounded as EditRoundedIcon,
  LinkRounded as LinkRoundedIcon,
  ListRounded as ListRoundedIcon,
  SentimentSatisfiedAltRounded as SentimentSatisfiedAltRoundedIcon,
  WarningRounded as WarningRoundedIcon
} from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  IconButton,
  Pagination,
  PaginationItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material'
import { t } from 'i18next'
import { useState } from 'react'
import Barcode from 'react-barcode'

import Can from '../../Layout/Can/Can.jsx'
import NotFoundMessage from '../../Shared/Components/NotFoundMessage.jsx'
import BarcodeDialog from '../Dialogs/Barcode.jsx'
import GatewayTests from '../Dialogs/GatewayTests.jsx'

const DEFAULT_PAGE_SIZE = 25

const GatewaysTable = ({ page, sorting, onPageChange, onSortingChange, isLoading, gateways, onEdit, onDelete }) => {
  const { direction, sort } = sorting

  const [selectedSerial, setSelectedSerial] = useState()
  const [gatewayTestsOpen, setGatewayTestsOpen] = useState(false)
  const [barcodeOpen, setBarcodeOpen] = useState(false)

  const handleChangeSorting = (field) =>
    onSortingChange({
      sort: field,
      direction: direction === 'asc' ? 'desc' : 'asc'
    })

  const handleGatewaysTestsOpen = (serial) => {
    setSelectedSerial(serial)
    onPageChange(1)
    setGatewayTestsOpen(true)
  }

  const handleGatewayTestsClose = () => {
    setSelectedSerial(null)
    setGatewayTestsOpen(false)
  }

  const handleBarcodeOpen = (serial) => {
    setSelectedSerial(serial)
    setBarcodeOpen(true)
  }

  const handleBarcodeClose = () => {
    setSelectedSerial(null)
    setBarcodeOpen(false)
  }

  const headerCells = [
    {
      isSortable: false,
      custom: false,
      customControl: null,
      name: 'reachable',
      label: t('gateways.reachable')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'gatewayId',
      label: t('gateways.gatewayId')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'wmbusId',
      label: t('gateways.wmbusId')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'serial',
      label: t('common.serial')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'status',
      label: t('common.status')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'technology',
      label: t('common.technology')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'address',
      label: t('common.address')
    },
    {
      isSortable: true,
      custom: false,
      customControl: null,
      name: 'isTested',
      label: t('common.actions')
    }
  ]

  const createCell = (cell) =>
    cell.isSortable ? (
      <TableSortLabel
        active={sort === cell.name}
        direction={sort === cell.name ? direction : 'asc'}
        onClick={() => handleChangeSorting(cell.name)}
        sx={{
          '&:hover': {
            textDecoration: 'underline'
          },
          '&.Mui-focusVisible': {
            textDecoration: 'underline'
          }
        }}
      >
        {cell.label}
      </TableSortLabel>
    ) : (
      cell.label
    )

  const tableHead = (headerCells) =>
    headerCells.map((cell, i) => <TableCell key={i}>{cell.custom ? cell.customControl : createCell(cell)}</TableCell>)

  return (
    <>
      {isLoading && (
        <Stack
          spacing={2}
          sx={{ marginBottom: '20px' }}>
          {[...Array(DEFAULT_PAGE_SIZE).keys()].map((index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rounded"
              sx={{ height: '100px' }}></Skeleton>
          ))}
        </Stack>
      )}

      {!isLoading && (

        <Table aria-label="gateways table" data-testid="gateways table">
          <TableHead>
            <TableRow>{tableHead(headerCells)}</TableRow>
          </TableHead>
          <TableBody>
            {gateways?.data.map((row) => (
              <TableRow
                key={row.serial}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  component="td"
                  scope="row">
                  {!row.reachable && (
                    <WarningRoundedIcon
                      size={16}
                      color="red"
                    />
                  )}

                  {row.reachable && (
                    <SentimentSatisfiedAltRoundedIcon
                      size={16}
                      color="green"
                    />
                  )}
                </TableCell>

                <TableCell
                  component="td"
                  scope="row">
                  <Typography
                    id={`gateway-id-${row.serial}`}
                    variant="p">{row.gatewayId}
                  </Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="p">{row.wmbusId}</Typography>
                </TableCell>

                <TableCell>
                  <Button
                    variant="text"
                    onClick={() => handleBarcodeOpen(row.serial)}>
                    <Barcode
                      value={row.serial}
                      width={1.4}
                      height={30}
                    />
                  </Button>
                </TableCell>

                <TableCell>
                  <Typography variant="p">{row.status}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="p">{row.technology}</Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="p">{row.address}</Typography>
                </TableCell>

                <TableCell size="medium">
                  <ButtonGroup>
                    <Can action={'gateways_u'}>
                      <IconButton
                        key="edit"
                        aria-label={`${t('gateways.edit')}`}
                        color="primary"
                        disabled={!row.fromCityeye}
                        onClick={() => onEdit(row.serial)}>
                        <EditRoundedIcon size={20} />
                      </IconButton>
                    </Can>

                    <Can action={'gateways_d'}>
                      <IconButton
                        key="delete"
                        aria-label={`${t('gateways.delete')}`}
                        color="error"
                        disabled={!row.fromCityeye}
                        onClick={() => onDelete(row.serial)}>
                        <DeleteRoundedIcon size={20} />
                      </IconButton>
                    </Can>
                    <Badge
                      badgeContent={row.isTested || 0}
                      color="primary"
                      showZero>
                      <IconButton
                        key="show tests"
                        disabled={row.isTested < 1}
                        aria-label={`${t('gateways.showTest')}`}
                        onClick={() => handleGatewaysTestsOpen(row.serial)}
                        color="primary">
                        <ListRoundedIcon size={20} />
                      </IconButton>
                    </Badge>

                    <IconButton
                      key="view in Salesforce"
                      disabled={!row.salesforceId}
                      href={`https://a2a-crm-b2b.lightning.force.com/${row.salesforceId}`}
                      target="_blank"
                      aria-label={`${t('gateways.viewSalesforce')}`}
                      rel="noopener noreferrer"
                      color="primary">
                      <LinkRoundedIcon size={20} />
                    </IconButton>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

      )}

      {gateways?.count > DEFAULT_PAGE_SIZE && (
        <Box
          display="flex"
          justifyContent="center">
          <Pagination
            count={Math.ceil(gateways?.count / DEFAULT_PAGE_SIZE)}
            page={page}
            onChange={onPageChange}
            renderItem={(item) => {
              const ariaLabels = {
                first: t('pagination.firstPage'),
                last: t('pagination.lastPage'),
                next: t('pagination.nextPage'),
                previous: t('pagination.previousPage'),
                page: t('pagination.goToPage', { page: item.page })
              }

              return (
                <PaginationItem
                  {...item}
                  aria-label={ariaLabels[item.type]}
                />
              )
            }}
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <NotFoundMessage when={() => !isLoading && !gateways?.data.length} />

      {gatewayTestsOpen && (
        <GatewayTests
          isOpen={gatewayTestsOpen}
          onClose={handleGatewayTestsClose}
          serial={selectedSerial}
        />
      )}

      {barcodeOpen && (
        <BarcodeDialog
          isOpen={barcodeOpen}
          onClose={handleBarcodeClose}
          serial={selectedSerial}
        />
      )}
    </>
  )
}

export default GatewaysTable
